import React, { useState, useEffect, useCallback } from 'react';
import './styles/TableSettingModal.css';
import CommonButton from '../elments/CommonButton';
import TableComponent from '../elments/TableComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';

const TableSettingModal = ({ isOpen, rows, onClose, onRowsChange, typeofproperty, netLabel, setNetLabel, createNet }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsLabel, setSelectedRowsLabel] = useState([]);
  const [reverseLabels, setReverseLabels] = useState(false);
  const [exclusive, setExclusive] = useState(false);
  
  // Function to handle logic change
  const handleLogicChange = (index, newLogic) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, Logic: newLogic } : row
    );
    onRowsChange(updatedRows);
  };

  const handleRowSelectionChange = (rowIndex, isSelected) => {
    const row = rows[rowIndex];
    const newSelectedRows = isSelected 
      ? [...selectedRows, row.index] 
      : selectedRows.filter(r => r !== row.index);
    const newSelectedRowsText = isSelected 
      ? [...selectedRowsLabel, row] 
      : selectedRowsLabel.filter(r => r.index !== row.index);
    
    setSelectedRows(newSelectedRows);
    setSelectedRowsLabel(newSelectedRowsText);
    setNetLabel("NET: " + newSelectedRowsText.map(item => item.title).join(' / '));
  };

  const handlePropertyChange = (index, newValue) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, title: newValue } : row
    );
    onRowsChange(updatedRows);
  };

  const handleFactorChange = (index, newFactor) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, factor: newFactor } : row
    );
    onRowsChange(updatedRows);
  };

  // Function for TableComponent's checkbox property change
  const handleTablePropertyChange = (property, itemType, index, value) => {
    if (property === 'select') {
      // Handle row selection
      handleRowSelectionChange(index, value);
    } else {
      // Handle other properties
      const updatedRows = rows.map((row, i) =>
        i === index ? { ...row, [property]: value } : row
      );
      onRowsChange(updatedRows);
    }
  };

  const handlePaste = (index, event, field) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text');
    const pastedRows = pasteData.split('\n').map(row => row.trim()).filter(row => row !== '');
  
    const updatedRows = [...rows];
    pastedRows.forEach((pastedRow, i) => {
      const rowIndex = index + i;
      if (rowIndex < updatedRows.length) {
        updatedRows[rowIndex] = { ...updatedRows[rowIndex], [field]: pastedRow };
      }
    });
    onRowsChange(updatedRows);
  };
  
  const moveRow = (fromIndex, toIndex) => {
    if (toIndex >= 0 && toIndex < rows.length) {
      const updatedRows = [...rows];
      const movedRow = updatedRows.splice(fromIndex, 1)[0];
      updatedRows.splice(toIndex, 0, movedRow);
      onRowsChange(updatedRows);
    }
  };

  const handleKeyDown = useCallback((event) => {
    if (selectedRows.length === 0) return;

    if (event.key === 'ArrowUp') {
      // Find the index of the first selected row
      const selectedIndex = rows.findIndex(row => row.index === selectedRows[0]);
      if (selectedIndex > 0) {
        moveRow(selectedIndex, selectedIndex - 1);
      }
    } else if (event.key === 'ArrowDown') {
      const selectedIndex = rows.findIndex(row => row.index === selectedRows[0]);
      if (selectedIndex < rows.length - 1) {
        moveRow(selectedIndex, selectedIndex + 1);
      }
    }
  }, [rows, selectedRows, onRowsChange]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const reverseValueLabels = () => {
    const updatedRows = [...rows].reverse();
    onRowsChange(updatedRows);
  };

  useEffect(() => {
    if (reverseLabels) {
      reverseValueLabels();
    }
  }, [reverseLabels]);

  const handleExclusive = () => {
    const updatedRows = rows.map(row =>
      selectedRows.includes(row.index) ? { ...row, exclusive: true } : row
    );
    onRowsChange(updatedRows);
    setExclusive(false);
    setSelectedRows([]);
    setSelectedRowsLabel([]);
  };

  if (!isOpen) return null;

  // Define custom columns for TableComponent with all required functionality
  const columns = [
    { 
      key: 'index', 
      label: 'Index', 
      width: '8%' 
    },
    { 
      key: 'label', 
      label: 'Label', 
      width: '25%',
      render: (item, index) => (
        <input
          type="text"
          value={item.title}
          title={item.title}
          onChange={(e) => handlePropertyChange(index, e.target.value)}
          onPaste={(e) => handlePaste(index, e, 'title')}
          className="title-input"
        />
      )
    },
    { 
      key: 'logic', 
      label: 'Logic', 
      width: '20%',
      render: (item, index) => (
        <input
          type="text"
          value={item.Logic || ''}
          onChange={(e) => handleLogicChange(index, e.target.value)}
          placeholder="Logic"
          className="logic-input"
        />
      )
    },
    { 
      key: 'factor', 
      label: 'Factor', 
      width: '20%',
      render: (item, index) => (
        <input
          type="text"
          value={item.factor || ''}
          onChange={(e) => handleFactorChange(index, e.target.value)}
          onPaste={(e) => handlePaste(index, e, 'factor')}
          className="factor-input"
        />
      )
    },
    { 
      key: 'select', 
      label: 'Select', 
      width: '7%', 
      type: 'checkbox',
      render: (item, index) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(item.index)}
          onChange={(e) => handleRowSelectionChange(index, e.target.checked)}
          className="row-select-checkbox"
        />
      )
    },
    { 
      key: 'move', 
      label: 'Move', 
      width: '20%',
      render: (item, index) => (
        <div className="move-buttons">
          <CommonButton 
            title="Move Row Up"
            label=""
            symbol={faArrowUp}
            onClick={() => moveRow(index, index - 1)}
            disabled={index === 0}
            variant="arrow"
            size="small"
          />
          <CommonButton 
            title="Move Row Down"
            label=""
            symbol={faArrowDown}
            onClick={() => moveRow(index, index + 1)}
            disabled={index === rows.length - 1}
            variant="arrow"
            size="small"
          />
        </div>
      )
    }
  ];

  // Prepare data for TableComponent
  const tableData = rows.map((row, index) => ({
    ...row,
    index: row.index || index,
    label: row.title,
    logic: row.Logic || '',
    factor: row.factor || '',
    select: selectedRows.includes(row.index || index)
  }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        
        <div className="table-container">
{/* === NET CREATION SECTION - MOVED TO TOP === */}
  {selectedRows.length > 0 && (
    <div className="net-creation-section">
      <div className="net-label-wrapper">
        <span className="net-prefix">NET:</span>
        <input
          type="text"
          placeholder="Enter custom net label (optional)"
          value={netLabel.replace(/^NET:\s*/, '')} // strip prefix for editing
          onChange={(e) => setNetLabel("NET: " + e.target.value)}
          className="net-input"
        />
      </div>
      <CommonButton 
        label={`Create Net (${selectedRows.length})`}
        onClick={() => {
          const finalLabel = netLabel.trim() || "NET: " + selectedRowsLabel.map(r => r.title).join(' / ');
          createNet(finalLabel, selectedRows);
          setSelectedRows([]);
          setSelectedRowsLabel([]);
          setNetLabel("NET: ");
        }}
        variant="success"
        size="medium"
      />
    </div>
  ) }          
          {/* Action Buttons Section */}
          <div className="action-buttons-section">
            {selectedRows.length > 0 && (
              <>
                <CommonButton 
                  title="Mark Selected Items as Exclusive"
                  label={`Mark Exclusive`}
                  onClick={handleExclusive}
                  variant="warning"
                  size="small"
                />
                <span className="selection-counter">
                  {selectedRows.length} selected
                </span>
              </>
            )}
            
            <div className="checkbox-section">
              <input
                type="checkbox"
                id="reverseLabels"
                checked={reverseLabels}
                onChange={(e) => setReverseLabels(e.target.checked)}
              />
              <label htmlFor="reverseLabels">
                Reverse {typeofproperty === 'rows' ? 'Rows' : 
                typeofproperty === 'cols' ? 'Columns' : 
                typeofproperty === 'values' ? 'Values' : 
                'Value Labels'}
              </label>
            </div>
          </div>

          {/* Single TableComponent with all functionality */}
          <div className="single-table-section">
            <TableComponent
              title={`${typeofproperty === 'rows' ? 'Rows' : 
                      typeofproperty === 'cols' ? 'Columns' : 
                      typeofproperty === 'values' ? 'Values' : 
                      'Value Labels'} Details`}
              items={tableData}
              itemType={typeofproperty}
              columns={columns}
              getCurrentLogic={() => ''}
              handleLogicChange={(index, value) => handleLogicChange(index, value)}
              handlePropertyChange={handleTablePropertyChange}
              isGridType={false}
              showAllProperties={false}
            />
          </div>
        </div>
      </div>
    
    </div>
  );
};

export default TableSettingModal;